import {
  Alert,
  Button,
  Card,
  message,
  Popconfirm,
  Segmented,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useState } from 'react';

import { formatMNT, queryErrorMessage } from '../CashTransactions/types';
import type { BatchDetail, BatchItem } from './types';
import { STATUS_TAGS } from './types';

const { Title } = Typography;

// One Batch transaction's checked rows. "Гүйлгээг хийх" records them all or none,
// and only when no row has an error; "Цуцлах" discards the batch.
export default function CashBatchDetail({
  batchId,
  canEdit,
  onBack,
}: {
  batchId: number;
  canEdit: boolean;
  onBack: () => void;
}) {
  const { runQuery } = useScreen();
  const [batch, setBatch] = useState<BatchDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'ALL' | 'ERROR'>('ALL');
  const [working, setWorking] = useState<'sync' | 'cancel' | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    runQuery<BatchDetail>('cash_batch_detail', { batchId })
      .then(([row]) => {
        setBatch(row ?? null);
        if (row && row.error_record > 0) setFilter('ERROR');
      })
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery, batchId]);

  useEffect(load, [load]);

  const act = (action: 'sync' | 'cancel') => {
    setWorking(action);
    runQuery(action === 'sync' ? 'cash_batch_sync' : 'cash_batch_cancel', { batchId })
      .then(() => {
        message.success(action === 'sync' ? 'Багц бүртгэгдлээ.' : 'Багц цуцлагдлаа.');
        load();
      })
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setWorking(null));
  };

  if (!batch) {
    return (
      <Card>
        <Button onClick={onBack} style={{ marginBottom: 16 }}>
          ← Буцах
        </Button>
        {loading && <Spin />}
      </Card>
    );
  }

  const open = batch.status === 'COMPARE';
  const hasErrors = batch.error_record > 0;
  const items = filter === 'ERROR' ? batch.items.filter((i) => i.status === 'ERROR') : batch.items;

  const columns: ColumnsType<BatchItem> = [
    { title: 'Мөр', dataIndex: 'row_number', width: 60 },
    { title: 'Код', dataIndex: 'student_code' },
    {
      title: 'Нэр (CSV)',
      dataIndex: 'full_name',
      render: (value: string, row) =>
        row.student_name && row.student_name !== value ? `${value} (${row.student_name})` : value,
    },
    {
      title: 'Төрөл',
      dataIndex: 'kind',
      render: (value: BatchItem['kind'], row) =>
        value ? (
          <Tag color={value === 'DEPOSIT' ? 'green' : 'red'}>
            {value === 'DEPOSIT' ? 'Орлого' : 'Зарлага'}
          </Tag>
        ) : (
          row.type_raw || '—'
        ),
    },
    {
      title: 'Дүн',
      dataIndex: 'amount',
      align: 'right',
      render: (value: number | null, row) =>
        value != null ? formatMNT(value) : row.amount_raw || '—',
    },
    {
      title: 'Үлдэгдэл',
      dataIndex: 'before_balance',
      align: 'right',
      render: (value: number | null, row) => {
        if (value == null || row.amount == null) return '—';
        const after = row.kind === 'DEPOSIT' ? value + row.amount : value - row.amount;
        return `${formatMNT(value)} → ${formatMNT(after)}`;
      },
    },
    { title: 'Тайлбар', dataIndex: 'description', render: (value: string | null) => value ?? '—' },
    {
      title: 'Төлөв',
      dataIndex: 'status',
      render: (value: BatchItem['status'], row) =>
        value === 'OK' ? (
          <Tag color="green">Зөв</Tag>
        ) : (
          <span>
            <Tag color="red">Алдаатай</Tag>
            <span style={{ color: '#cf1322' }}>{row.message}</span>
          </span>
        ),
    },
  ];

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Button onClick={onBack}>← Буцах</Button>
          <Title level={4} style={{ margin: 0 }}>
            Багц #{batch.id} · {batch.file_name}
          </Title>
          <Tag color={STATUS_TAGS[batch.status]?.color}>{STATUS_TAGS[batch.status]?.label}</Tag>
        </div>
        {canEdit && open && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Popconfirm
              title="Багцыг цуцлах уу?"
              description="Картад юу ч бүртгэгдэхгүй."
              okText="Цуцлах"
              okButtonProps={{ danger: true }}
              cancelText="Болих"
              onConfirm={() => act('cancel')}
            >
              <Button danger loading={working === 'cancel'} disabled={working != null}>
                Цуцлах
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Гүйлгээг хийх үү?"
              description={`${batch.total_record} гүйлгээ картуудад бүртгэгдэнэ. Орлого ${formatMNT(batch.deposit_total)}, зарлага ${formatMNT(batch.withdrawal_total)}. Энэ үйлдлийг буцаах боломжгүй.`}
              okText="Гүйлгээг хийх"
              cancelText="Болих"
              onConfirm={() => act('sync')}
              disabled={hasErrors}
            >
              <Button
                type="primary"
                loading={working === 'sync'}
                disabled={hasErrors || working != null}
              >
                Гүйлгээг хийх
              </Button>
            </Popconfirm>
          </div>
        )}
      </div>

      {batch.duplicate_batch_ids.length > 0 && open && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Яг ижил файл өмнө нь бүртгэгдсэн байна: ${batch.duplicate_batch_ids
            .map((id) => `Багц #${id}`)
            .join(', ')}. Давхар бүртгэхгүй эсэхээ шалгана уу.`}
        />
      )}
      {hasErrors && open && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${batch.error_record} мөр алдаатай тул бүртгэх боломжгүй. Файлаа засаад дахин оруулна уу.`}
        />
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, marginBottom: 16 }}>
        <Statistic title="Нийт мөр" value={batch.total_record} />
        <Statistic
          title="Орлого"
          value={batch.deposit_total}
          formatter={(v) => formatMNT(Number(v))}
          valueStyle={{ color: '#389e0d' }}
        />
        <Statistic
          title="Зарлага"
          value={batch.withdrawal_total}
          formatter={(v) => formatMNT(Number(v))}
          valueStyle={{ color: '#cf1322' }}
        />
      </div>

      <Segmented
        style={{ marginBottom: 12 }}
        value={filter}
        onChange={(value) => setFilter(value as 'ALL' | 'ERROR')}
        options={[
          { label: `Бүгд (${batch.items.length})`, value: 'ALL' },
          { label: `Алдаатай (${batch.error_record})`, value: 'ERROR' },
        ]}
      />
      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={items}
        loading={loading}
        scroll={{ x: true }}
        pagination={{ pageSize: 50, showSizeChanger: false }}
      />
    </Card>
  );
}
